<script>
import logo from "@/mixins/logo";
import authService from "@/services/auth";
import centerService from "@/services/center";
import { version } from "../../../package.json";
import { mapGetters } from "vuex";

/**
 * @component Login
 * @description Pantalla de login. Soporta DOS rutas:
 *
 *   /login              → branding del tenant (logo Quantux/Vilella). Pensada
 *                         para staff Quantux interno y casos genéricos.
 *   /login/:orgSlug     → branding de la Organization indicada. Pensada para
 *                         que cada cliente white-label tenga su propia URL de
 *                         entrada (ej. /login/cnc) sin necesidad de
 *                         subdominio dedicado. Si el slug no existe el
 *                         componente cae silenciosamente al branding del
 *                         tenant — NO redirige para no exponer la URL alt.
 *
 * UC-REG-077.
 */
export default {
  name: "Login",

  mixins: [logo],

  data() {
    return {
      isLoading: false,
      version,
      username: "",
      password: "",
      // Branding fetchado del endpoint público /landing/center/:slug.
      // null cuando: no hay slug en la URL, o el slug no resuelve.
      orgBranding: null,
      orgName: null,
      // Loading mientras fetchamos el branding — no rendereamos el form
      // sin esto para evitar flicker "Quantux → CNC" del logo.
      isBrandingLoading: false,
    };
  },

  computed: {
    ...mapGetters(["isPatient"]),

    /**
     * @description Slug de la org tomado del path /login/:orgSlug. Null si
     *              estamos en /login plano.
     */
    orgSlug() {
      return (this.$route.params && this.$route.params.orgSlug) || null;
    },

    /**
     * @description URL final del logo a mostrar en el login. Prioridad:
     *              org.branding.logoUrl > theme assets > fallback Quantux.
     */
    effectiveLogoUrl() {
      if (this.orgBranding && this.orgBranding.logoUrl) {
        return this.orgBranding.logoUrl;
      }
      // theme === 'vilella' usa el logoAltUrl del mixin (legacy)
      if (this.theme === "vilella" && this.logoAltUrl) {
        return this.logoAltUrl;
      }
      return null;
    },
  },

  async created() {
    if (this.orgSlug) {
      this.isBrandingLoading = true;
      // Evitar indexación de la URL /login/:slug — es operativa, no contenido
      // publico (la publica del centro va por /centro/:slug en el landing).
      this.applyNoIndexMeta();
      const center = await centerService.getBySlug(this.orgSlug);
      if (center && center.center) {
        this.orgBranding = center.center.branding || null;
        this.orgName = center.center.name || null;
        this.applyOrgColors();
      }
      this.isBrandingLoading = false;
    }
  },

  beforeDestroy() {
    // Limpiar CSS vars al salir del login para que el dashboard no herede
    // los colores que seteamos a mano (App.vue los maneja con su propia
    // lógica una vez que el user logue).
    if (typeof document !== "undefined") {
      const root = document.documentElement;
      root.style.removeProperty("--org-color-primary");
      root.style.removeProperty("--org-color-accent");
    }
    // Quitar meta noindex al salir del login.
    if (typeof document !== "undefined") {
      const existing = document.querySelector(
        "meta[name='robots'][data-login-noindex]"
      );
      if (existing) existing.remove();
    }
  },

  methods: {
    /**
     * @description Aplica los colores de la org como CSS custom properties.
     *              Replica la lógica de App.vue pero en runtime del login
     *              (donde todavía no hay user logueado ni organizationContext).
     */
    applyOrgColors() {
      if (typeof document === "undefined" || !this.orgBranding) return;
      const root = document.documentElement;
      if (this.orgBranding.colorPrimary) {
        root.style.setProperty(
          "--org-color-primary",
          this.orgBranding.colorPrimary
        );
      }
      if (this.orgBranding.colorAccent) {
        root.style.setProperty(
          "--org-color-accent",
          this.orgBranding.colorAccent
        );
      }
    },

    /**
     * @description Inserta meta robots=noindex,nofollow en el head para que
     *              la URL /login/:slug no sea indexada por buscadores.
     */
    applyNoIndexMeta() {
      if (typeof document === "undefined") return;
      // Evitar duplicado si ya existe.
      if (document.querySelector("meta[name='robots'][data-login-noindex]")) {
        return;
      }
      const m = document.createElement("meta");
      m.setAttribute("name", "robots");
      m.setAttribute("content", "noindex,nofollow");
      m.setAttribute("data-login-noindex", "1");
      document.head.appendChild(m);
    },

    /**
     * @description Autentica al usuario. Si es paciente redirige a patient-me,
     *              de lo contrario al home o a la redirectUrl del query string.
     */
    signIn() {
      // Validación previa con feedback claro — sin esto los campos vacíos
      // solo deshabilitaban el botón en silencio (bug BUG-009).
      if (!this.username) {
        this.$message.error("El usuario es requerido");
        return;
      }
      if (!this.password) {
        this.$message.error("La contraseña es requerida");
        return;
      }
      this.isLoading = true;
      authService
        .authenticate(this.username, this.password)
        .then(() => {
          if (this.isPatient) {
            this.$router.push({ name: "patient-me" });
            return;
          }
          const path =
            this.$route.query && this.$route.query.redirectUrl
              ? this.$route.query.redirectUrl
              : { name: "home" };
          this.$router.push(path);
        })
        .catch((err) => {
          if (err && err.message === "INVALID_AUDIENCE") {
            this.$message.error(
              "Tu usuario no tiene acceso a esta plataforma."
            );
          } else {
            // 401 y demás errores: mensaje genérico que no permita
            // enumeración de usuarios — el backend ya responde 401 igual
            // para usuario inexistente y password incorrecta.
            this.$message.error("Usuario o contraseña incorrectos");
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },

  metaInfo() {
    // Evitar que Google indexe la URL /login/:slug — es operativa, no
    // contenido público. La pública del centro va por /centro/:slug en el
    // landing (otro repo).
    return this.orgSlug
      ? {
          meta: [{ name: "robots", content: "noindex,nofollow" }],
        }
      : {};
  },
};
</script>

<template lang="pug">
  .login
    .login__container
      form.login__card(@submit.prevent="signIn" v-if="!isBrandingLoading")
        .login__img
          img.login__svg(v-if="effectiveLogoUrl" :src="effectiveLogoUrl" :alt="orgName || 'Logo'")
          micon.login__svg(v-else :name="'quantux-logo-horizontal-nuevo'")
        h2.login__org-name(v-if="orgName") {{ orgName }}
        el-input#username(placeholder="Ingresá tu usuario o DNI" autofocus="true" v-model="username" autocapitalize="none")
        el-input#password(type="password" placeholder="Contraseña" v-model="password" show-password)
        el-button(type="primary" native-type="submit" :loading="isLoading") Iniciar Sesión
        router-link.login__link(:to="{ name: 'password-forgot' }") ¿Olvidaste tu contraseña?

        small.version v.{{ version }}
</template>

<style lang="scss">
.login {
  .version {
    position: absolute;
    bottom: 10px;
    right: 10px;
  }

  .login__img {
    margin-bottom: 20px;
    display: flex;
    justify-content: center;
  }

  .login__svg {
    max-width: 300px !important;
    max-height: 60px !important;
    height: auto !important;
    width: auto !important;
  }

  .login__org-name {
    text-align: center;
    margin: 0 0 24px;
    color: var(--org-color-primary, #002d5e);
    font-weight: 600;
    font-size: 18px;
  }

  .el-input {
    margin-bottom: 10px;
  }

  button {
    width: 100%;
  }

  // Usar color primario de la org si está seteado por applyOrgColors().
  // El default del tema queda como fallback.
  .el-button--primary {
    background-color: var(--org-color-primary, #002d5e);
    border-color: var(--org-color-primary, #002d5e);
  }

  .el-button--primary:hover {
    background-color: var(--org-color-accent, #0a3d7a);
    border-color: var(--org-color-accent, #0a3d7a);
  }
}
</style>
